"use client";
import { useScroll } from "@/libs/scroll-detect";
import {
  CloseButton,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/react";
import {
  Bars3Icon,
  XMarkIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useState } from "react";

function classNames(...classes: any) {
  return classes.filter(Boolean).join(" ");
}

export default function NavbarComp({ menu }: { menu: any[] }) {
  const router = useRouter();
  const pathname = usePathname();
  const y = pathname.split("/");
  const rootPath =
    y && y.length > 1 ? (y[0] === "" && y[1] === "" ? "beranda" : y[1]) : "";
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [isScroll] = useScroll({ threshold: 60 });

  const onSearch = (event: any) => {
    if (event.key === "Enter") {
      const params = new URLSearchParams({ keyword }).toString();
      router.push(`/search?${params}`);
      setKeyword("");
    }
  };

  return (
    <Disclosure
      as="nav"
      className={
        isScroll
          ? "bg-white bg-opacity-85 fixed top-0 w-full z-50"
          : "bg-transparent fixed top-0 w-full z-50"
      }
    >
      <div className="container mx-auto">
        <div className="relative flex h-16 items-center justify-between">
          <div className="absolute inset-y-0 left-0 flex items-center lg:hidden">
            <DisclosureButton
              className={`group relative inline-flex items-center justify-center rounded-md p-2  border border-solid  bg-white bg-opacity-15 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white ${
                isScroll ? "border-green text-green" : "text-white border-white"
              }`}
            >
              <span className="absolute -inset-0.5" />
              <span className="sr-only">Menu</span>
              <Bars3Icon
                aria-hidden="true"
                className="block size-6 group-data-[open]:hidden"
              />
              <XMarkIcon
                aria-hidden="true"
                className="hidden size-6 group-data-[open]:block"
              />
            </DisclosureButton>
          </div>
          <div className="flex flex-1 items-center justify-center lg:justify-start">
            <div className="flex shrink-0 items-center">
              <Link href="/">
                <img
                  alt="Kementreian Kehutanan"
                  src={isScroll ? "/images/logo.png" : "/images/logo-white.png"}
                  className={`h-10 lg:h-12 w-auto ${
                    showSearchInput ? "hidden" : "block"
                  }`}
                />
              </Link>
            </div>
            <div className="hidden lg:ml-6 lg:block">
              <div className="flex space-x-4">
                {menu.map((item) =>
                  item.childs.length > 0 ? (
                    <Menu as="div" className="relative ml-3" key={item.name}>
                      <div>
                        <MenuButton
                          className={classNames(
                            item.path === rootPath
                              ? "bg-green text-white"
                              : "hover:bg-green hover:text-white",
                            "rounded-md px-3 py-2 text-sm font-medium cursor-pointer",
                            isScroll ? "text-green" : "text-white"
                          )}
                        >
                          <span className="absolute -inset-1.5" />
                          <span className="sr-only">{item.name}</span>
                          <span>{item.name}</span>
                        </MenuButton>
                      </div>
                      <MenuItems
                        transition
                        className="absolute cursor-pointer right-0 z-10 mt-5 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                      >
                        {item.childs.map((ic: any) => (
                          <MenuItem key={ic.name}>
                            <Link
                              href={ic.href}
                              className="block cursor-pointer px-4 py-2 text-sm text-dark data-[focus]:outline-none hover:bg-[#d9eae3]"
                            >
                              {ic.name}
                            </Link>
                          </MenuItem>
                        ))}
                      </MenuItems>
                    </Menu>
                  ) : (
                    <Link
                      key={item.name}
                      href={item.href}
                      aria-current={item.path === rootPath ? "page" : undefined}
                      className={classNames(
                        item.path === rootPath
                          ? "bg-green text-white"
                          : "hover:bg-green hover:text-white",
                        "rounded-md px-3 py-2 text-sm font-medium cursor-pointer",
                        isScroll ? "text-green" : "text-white"
                      )}
                    >
                      {item.name}
                    </Link>
                  )
                )}
              </div>
            </div>
          </div>
          <div className="absolute inset-y-0 right-0 flex items-center lg:static lg:inset-auto">
            {/* Mobile View */}
            <button
              type="button"
              className={`lg:hidden relative rounded-full p-2 text-white bg-white bg-opacity-15 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white ${
                showSearchInput ? "hidden" : "block"
              }`}
              onClick={() => setShowSearchInput(true)}
            >
              <span className="absolute -inset-1.5" />
              <span className="sr-only">Search</span>
              <MagnifyingGlassIcon
                aria-hidden="true"
                className={`size-6 ${isScroll ? "text-green" : "text-white"}`}
              />
            </button>

            {/* Desktop View */}
            <div
              className={`lg:block relative ${
                showSearchInput ? "block" : "hidden"
              }`}
            >
              <input
                type="text"
                name="search"
                placeholder="Pencarian"
                className={`h-9 bg-white bg-opacity-15 border border-solid rounded-3xl focus:outline-none ps-4 pe-8 w-60 focus:w-72 transition-all duration-300 ease-in-out ${
                  isScroll
                    ? "border-green text-green placeholder-green"
                    : "border-white text-white placeholder-white"
                }`}
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                onKeyDown={onSearch}
                autoComplete="off"
              />

              {showSearchInput ? (
                <XMarkIcon
                  aria-hidden="true"
                  className={`size-5 absolute right-2 top-2 ${
                    isScroll ? "text-green" : "text-white"
                  }`}
                  onClick={() => setShowSearchInput(false)}
                />
              ) : (
                <MagnifyingGlassIcon
                  aria-hidden="true"
                  className={`size-5 absolute right-2 top-2 ${
                    isScroll ? "text-green" : "text-white"
                  }`}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <DisclosurePanel className="lg:hidden">
        <div className="space-y-1 px-2 pb-3 pt-2 bg-white bg-opacity-90">
          {menu.map((item) =>
            item.childs.length > 0 ? (
              <Menu as="div" className="relative w-full" key={item.name}>
                <div>
                  <MenuButton
                    className={classNames(
                      item.path === rootPath
                        ? "bg-green text-white"
                        : "hover:bg-green hover:text-white",
                      "block rounded-md px-3 py-2 text-base font-medium text-dark w-full text-left"
                    )}
                  >
                    <span className="sr-only">{item.name}</span>
                    <span>{item.name}</span>
                  </MenuButton>
                </div>
                <MenuItems
                  transition
                  className="mt-2 w-full origin-top-right py-1 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                >
                  {item.childs.map((ic: any) => (
                    <MenuItem key={ic.name}>
                      <CloseButton
                        as={Link}
                        href={ic.href}
                        className="block cursor-pointer px-4 py-2 text-dark data-[focus]:outline-none hover:bg-[#d9eae3]"
                      >
                        {ic.name}
                      </CloseButton>
                    </MenuItem>
                  ))}
                </MenuItems>
              </Menu>
            ) : (
              <CloseButton
                as={Link}
                key={item.name}
                href={item.href}
                aria-current={item.path === rootPath ? "page" : undefined}
                className={classNames(
                  item.path === rootPath
                    ? "bg-green text-white"
                    : "hover:bg-green hover:text-white",
                  "block rounded-md px-3 py-2 text-base font-medium text-dark cursor-pointer"
                )}
              >
                {item.name}
              </CloseButton>
            )
          )}
        </div>
      </DisclosurePanel>
    </Disclosure>
  );
}
